import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { useIsMobile } from '@/lib/hooks'
import { cn } from '@/lib/utils'

import {
  Button,
  KeyChip,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui'

import { Calendar } from './calendar'
import FloatingKeyboard from './keyboard'

const inputVariants = cva(
  'flex h-9 w-full rounded-md border dark:border-gray-700 border-gray-100 bg-transparent bg-white px-3 py-1 transition-colors file:border-0 file:bg-transparent file:text-sm text-sm placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {},
      inputSize: {
        default: 'h-11 px-4 py-2 rounded-lg',
        sm: 'h-8 rounded-lg px-3 text-xs',
        lg: 'h-[52px] rounded-lg px-4',
      },
    },
    defaultVariants: {
      inputSize: 'default',
    },
  },
)

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement>,
    VariantProps<typeof inputVariants> {
  onChangeValue?: (value: string) => void
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, inputSize, onChangeValue, value, ...props }, ref) => {
    const isMobile = useIsMobile()
    const [inputValue, setInputValue] = React.useState(value || '')
    const [isOpen, setIsOpen] = React.useState(false)
    const showVirtualKeyboard = localStorage?.getItem('showVirtualKeyboard')

    const handleNumberInput = (value: string) => {
      // Allow empty string, numbers, and decimal points
      if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
        setInputValue(value)
        onChangeValue?.(value)
      }
    }

    React.useEffect(() => {
      setInputValue(value || '')
    }, [value])

    const handleKeyPress = (value: string) => {
      if (value === '⌫') {
        const newValue = String(inputValue).slice(0, -1)
        setInputValue(newValue)
        onChangeValue?.(newValue)
      } else if (value === 'clear') {
        setInputValue('')
        onChangeValue?.('')
      } else {
        setInputValue((prev) => prev + value)
        onChangeValue?.(inputValue + value)
      }
    }

    if (isMobile || showVirtualKeyboard === 'false') {
      return (
        <input
          type={type === 'number' ? 'text' : type}
          className={cn(inputVariants({ inputSize, className }))}
          value={inputValue}
          onChange={(e) => {
            type === 'number'
              ? handleNumberInput(e.target.value)
              : (() => {
                  setInputValue(e.target.value)
                  onChangeValue?.(e.target.value)
                })()
          }}
          ref={ref}
          {...props}
        />
      )
    }

    return (
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <input
            type={type === 'number' ? 'text' : type}
            className={cn(inputVariants({ inputSize, className }))}
            value={inputValue}
            onChange={(e) => {
              type === 'number'
                ? handleNumberInput(e.target.value)
                : (() => {
                    setInputValue(e.target.value)
                    onChangeValue?.(e.target.value)
                  })()
            }}
            ref={ref}
            {...props}
          />
        </PopoverTrigger>

        <PopoverContent className="w-full overflow-hidden p-0">
          {type === 'datepicker' ? (
            <Calendar
              mode="single"
              selected={
                inputValue ? new Date(inputValue.toString()) : undefined
              }
              onSelect={(day) => {
                if (day) {
                  const formattedDate = day.toISOString().split('T')[0]
                  setInputValue(formattedDate)
                  onChangeValue?.(formattedDate)
                  setIsOpen(false)
                }
              }}
            />
          ) : (
            <FloatingKeyboard
              type={type}
              isNumberOnly={type === 'number'}
              onKeyPress={handleKeyPress}
            />
          )}
        </PopoverContent>
      </Popover>
    )
  },
)
Input.displayName = 'Input'

export { Input }
